/**
 * User-Friendly Error Messages
 * Section 5.4 - API Error Handling
 *
 * Maps API error codes to human-readable messages
 */

const ERROR_MESSAGES = {
  // Vehicle selection errors
  VEHICLE_NOT_SUPPORTED: 'This vehicle is not supported for our service.',
  OIL_CAPACITY_NOT_FOUND: 'Oil capacity not found for this vehicle. Please try a different configuration.',
  ENGINE_DATA_INCOMPLETE: 'Engine data is incomplete. Please try a different year or model.',
  NO_ENGINES_FOUND: 'No engines found for this vehicle configuration.',

  // Booking errors
  SLOT_UNAVAILABLE: 'This time slot was just booked by another customer. Please select a different time.',
  SLOT_ALREADY_BOOKED: 'This time slot is already booked. Please choose another time.',
  INVALID_DATE: 'Please select a valid date for your appointment.',
  PAST_DATE: 'Cannot book appointments in the past. Please select a future date.',

  // Service area errors
  OUTSIDE_SERVICE_AREA: 'We are not providing service in that area yet. Please call (916) 818-8494 for questions.',

  // Form validation errors
  INVALID_YEAR: 'Please select a valid year (2003-2026).',
  MISSING_REQUIRED: 'Please fill in all required fields.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  INVALID_PHONE: 'Please enter a valid phone number.',
  INVALID_ZIP: 'Please enter a valid 5-digit ZIP code.',

  // Network/Server errors
  NETWORK_ERROR: 'Unable to connect. Please check your internet connection and try again.',
  SERVER_ERROR: 'Something went wrong on our end. Please try again in a moment.',
  TIMEOUT: 'The request took too long. Please try again.',

  // Default
  UNKNOWN: 'An unexpected error occurred. Please try again or contact us for assistance.'
};

/**
 * Get a user-friendly error message for an error code
 * @param {string} code - The error code from API
 * @param {string} fallbackMessage - Optional fallback message
 * @returns {string} User-friendly error message
 */
export function getErrorMessage(code, fallbackMessage = null) {
  if (ERROR_MESSAGES[code]) {
    return ERROR_MESSAGES[code];
  }
  return fallbackMessage || ERROR_MESSAGES.UNKNOWN;
}

/**
 * Parse an error response and return user-friendly message
 * @param {Error|Response|Object} error - The error object
 * @returns {string} User-friendly error message
 */
export function parseError(error) {
  // Handle fetch Response object
  if (error?.status) {
    switch (error.status) {
      case 400:
        return ERROR_MESSAGES.MISSING_REQUIRED;
      case 404:
        return ERROR_MESSAGES.VEHICLE_NOT_SUPPORTED;
      case 409:
        return ERROR_MESSAGES.SLOT_UNAVAILABLE;
      case 500:
        return ERROR_MESSAGES.SERVER_ERROR;
      default:
        return ERROR_MESSAGES.UNKNOWN;
    }
  }

  // Handle error with code property
  if (error?.code) {
    return getErrorMessage(error.code, error.message);
  }

  // Handle error with message property
  if (error?.message) {
    // Check if message contains known patterns
    if (error.message.includes('network') || error.message.includes('fetch')) {
      return ERROR_MESSAGES.NETWORK_ERROR;
    }
    if (error.message.includes('timeout')) {
      return ERROR_MESSAGES.TIMEOUT;
    }
    return error.message;
  }

  // Handle string error
  if (typeof error === 'string') {
    return error;
  }

  return ERROR_MESSAGES.UNKNOWN;
}

/**
 * Field-specific validation error messages
 */
export const FIELD_ERRORS = {
  name: 'Please enter your name.',
  email: 'Please enter a valid email address.',
  phone: 'Please enter a valid phone number.',
  address: 'Please enter your address.',
  city: 'Please enter your city.',
  zipCode: 'Please enter a valid ZIP code.',
  vehicleYear: 'Please select a year.',
  vehicleMake: 'Please select a make.',
  vehicleModel: 'Please select a model.',
  vehicleEngine: 'Please select an engine.',
  vehicleColor: 'Please select a vehicle color.',
  paymentMethod: 'Please select a payment method.',
  terms: 'You must agree to the terms to continue.'
};

const errorUtils = {
  getErrorMessage,
  parseError,
  FIELD_ERRORS,
  ERROR_MESSAGES
};

export default errorUtils;
